import { Alert } from "@/src/lib/alert";
import { Link } from "expo-router";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { BrandIntro } from "@/src/components/brand/BrandIntro";
import { AuthField } from "@/src/components/ui/AuthField";
import { PrimaryButton } from "@/src/components/ui/PrimaryButton";
import { ScreenContainer } from "@/src/components/ui/ScreenContainer";
import { auth } from "@/src/lib/firebase";
import { emailVerificationErrorMessage } from "@/src/lib/emailVerification";
import { spacing, text, typography } from "@/src/theme/tokens";

function registrationErrorMessage(error: unknown) {
  const code = String((error as { code?: unknown } | null)?.code ?? "");
  if (code === "auth/invalid-email") return "Adresse email invalide.";
  if (code === "auth/email-already-in-use") return "Un compte utilise déjà cette adresse email.";
  if (code === "auth/weak-password") return "Choisis un mot de passe plus robuste.";
  if (code === "auth/network-request-failed") return "Connexion impossible. Vérifie ton réseau.";
  return "Impossible de créer le compte. Réessaie dans un instant.";
}

export default function RegisterScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function onRegister() {
    if (!email || !password) {
      Alert.alert("Erreur", "Email et mot de passe requis.");
      return;
    }
    if (password.length < 6) {
      Alert.alert("Erreur", "Mot de passe : 6 caractères minimum.");
      return;
    }
    try {
      setBusy(true);
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      try {
        await sendEmailVerification(credential.user);
      } catch (verificationError: unknown) {
        Alert.alert(
          "Compte créé",
          `${emailVerificationErrorMessage(verificationError)} Tu pourras renvoyer le lien depuis l’écran de vérification.`
        );
      }
    } catch (error: unknown) {
      Alert.alert("Inscription impossible", registrationErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ScreenContainer contentStyle={styles.content}>
      <View style={styles.intro}>
        <BrandIntro compact />
        <View style={styles.headingGroup}>
          <Text style={styles.heading}>Rejoins Neqraw.</Text>
          <Text style={styles.body}>Crée ton compte, puis choisis ton profil académique.</Text>
        </View>
      </View>

      <View style={styles.form}>
        <AuthField
          autoCapitalize="none"
          autoComplete="email"
          autoCorrect={false}
          keyboardType="email-address"
          label="Email"
          onChangeText={setEmail}
          placeholder="nom@exemple.com"
          returnKeyType="next"
          value={email}
        />
        <AuthField
          autoComplete="new-password"
          hint="6 caractères minimum"
          label="Mot de passe"
          onChangeText={setPassword}
          onSubmitEditing={() => void onRegister()}
          placeholder="Choisis un mot de passe"
          returnKeyType="done"
          secureTextEntry
          value={password}
        />
        <PrimaryButton busy={busy} busyLabel="Création…" label="Créer le compte" onPress={() => void onRegister()} />
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>Tu as déjà un compte ?</Text>
        <Link href="/(auth)/login" asChild>
          <Pressable accessibilityRole="link" hitSlop={8}>
            <Text style={styles.link}>Se connecter</Text>
          </Pressable>
        </Link>
      </View>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  content: { justifyContent: "center", gap: spacing.xxl },
  intro: { gap: spacing.xl },
  headingGroup: { gap: spacing.sm },
  heading: { color: text.primary, fontFamily: typography.family.semibold, fontSize: typography.size.heading, lineHeight: typography.lineHeight.heading },
  body: { color: text.secondary, fontFamily: typography.family.regular, fontSize: typography.size.body, lineHeight: typography.lineHeight.body, maxWidth: 420 },
  form: { gap: spacing.lg },
  footer: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, justifyContent: "center" },
  footerText: { color: text.secondary, fontFamily: typography.family.regular, fontSize: 15, lineHeight: 22 },
  link: { color: text.brand, fontFamily: typography.family.semibold, fontSize: 15, lineHeight: 22 },
});
